import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel, EASE } from "./shared";
import { scrollToId } from "./Header";
import { useLang } from "./i18n";

export function Pricing() {
  const [tab, setTab] = useState("purchase");
  const { t } = useLang();
  const plans = t.pricing.plans[tab];

  return (
    <section id="pricing" className="bg-paper py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <SectionLabel>{t.pricing.label}</SectionLabel>
          <h2 className="font-display text-4xl font-semibold leading-[1.1] tracking-[-0.02em] text-ink md:text-5xl">
            {t.pricing.titleA} <span className="italic text-aqua-700">{t.pricing.titleB}</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-slate-500">{t.pricing.sub}</p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-10 inline-flex rounded-full border border-slate-200 bg-white p-1.5">
            {t.pricing.tabs.map((tb) => (
              <button
                key={tb.id}
                data-testid={`pricing-tab-${tb.id}`}
                onClick={() => setTab(tb.id)}
                className={`rounded-full px-5 py-2.5 text-[13px] font-bold transition-colors duration-200 ${
                  tab === tb.id ? "bg-navy text-white" : "text-slate-500 hover:text-ink"
                }`}
              >
                {tb.label}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="mt-12 grid gap-6 lg:grid-cols-3"
          >
            {plans.map((plan, i) => (
              <div
                key={i}
                data-testid={`pricing-card-${tab}-${i}`}
                className={`relative flex flex-col rounded-3xl p-8 transition-transform duration-300 hover:-translate-y-1.5 ${
                  plan.popular
                    ? "bg-navy text-white shadow-[0_32px_80px_rgba(27,42,76,0.35)] lg:scale-[1.04]"
                    : "border border-slate-200 bg-white"
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3.5 left-8 rounded-full bg-maple px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-white">
                    {t.pricing.popular}
                  </span>
                )}
                <h3 className={`font-display text-2xl font-semibold ${plan.popular ? "text-white" : "text-ink"}`}>
                  {plan.name}
                </h3>
                <p className={`mt-1 text-sm ${plan.popular ? "text-mist" : "text-slate-400"}`}>{plan.subtitle}</p>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className={`font-display text-5xl font-semibold ${plan.popular ? "text-maple" : "text-ink"}`}>
                    {plan.price}
                  </span>
                </div>
                <p className={`mt-2 text-xs ${plan.popular ? "text-mist" : "text-slate-400"}`}>{plan.note}</p>
                <ul className="mt-7 flex-1 space-y-3">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm">
                      <Check
                        size={15}
                        strokeWidth={3}
                        className={`mt-0.5 shrink-0 ${plan.popular ? "text-aqua-300" : "text-aqua-600"}`}
                      />
                      <span className={plan.popular ? "text-white/85" : "text-slate-600"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`pricing-cta-${tab}-${i}`}
                  onClick={() => scrollToId("#quote")}
                  className={`group mt-8 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-bold transition-colors duration-200 ${
                    plan.popular
                      ? "bg-maple text-white hover:bg-[#C23225]"
                      : "border border-navy/20 text-navy hover:border-navy hover:bg-navy hover:text-white"
                  }`}
                >
                  {plan.price === "Custom" || plan.price === "Sur mesure" ? t.pricing.contactUs : t.pricing.getStarted}
                  <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                </button>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
